import { useMemo } from "react";
import {
    Service as BaseService,
    useService as useBaseService,
    Resource,
} from "./Base";

export enum TTSVoice {
    ALLOY = "alloy",
    ECHO = "echo",
    FABLE = "fable",
    ONYX = "onyx",
    NOVA = "nova",
    SHIMMER = "shimmer",
}

export enum AudioDocumentStatus {
    PROCESSING = "processing",
    READY = "ready",
    FAILED = "failed",
}

interface CreateParams {
    text: string;
    voice?: TTSVoice;
}

interface AudioChunk {
    id: string;
    index: number;
    durationSeconds: number;
    characterCount: number;
}

export interface AudioDocument extends Omit<Resource, "object"> {
    object: "audio_document";
    text: string;
    description?: string;
    voice: TTSVoice;
    status: AudioDocumentStatus;
    totalDurationSeconds?: number;
    finalAudioS3Key?: string;
    finalAudioDurationSeconds?: number;
    errorMessage?: string;
    chunks?: AudioChunk[];
}

interface ListParams {
    skip?: number;
    limit?: number;
}

interface ListResponse {
    object: "list";
    skip: number;
    limit: number;
    totalCount: number;
    data: AudioDocument[];
}

interface ChunkStreamResponse {
    object: "audio_chunk_stream";
    url: string;
    chunkIndex: number;
    durationSeconds: number;
}

interface UpdatePlaybackParams {
    audioDocumentId: string;
    currentChunkIndex: string;
    positionSeconds: string;
}

interface PlaybackState {
    object: "playback_state";
    id: string;
    audioDocumentId: string;
    currentChunkIndex: number;
    positionSeconds: number;
    totalPlaybackSeconds?: number;
    lastPlayedAt: Date;
}

export class Service {
    base: BaseService;
    route: string;

    constructor(service: BaseService, route: string) {
        this.base = service;
        this.route = route;
    }

    async create(data: CreateParams) {
        return await this.base.fetchAPI<AudioDocument>(`${this.route}/`, {
            method: "POST",
            body: {
                text: data.text,
                voice: data.voice || TTSVoice.ALLOY,
            },
        });
    }

    async list(params?: ListParams): Promise<ListResponse> {
        const queryParams = new URLSearchParams();
        if (params?.skip !== undefined) {
            queryParams.append("skip", params.skip.toString());
        }
        if (params?.limit !== undefined) {
            queryParams.append("limit", params.limit.toString());
        }

        const queryString = queryParams.toString();
        const url = queryString
            ? `${this.route}/?${queryString}`
            : `${this.route}/`;

        return await this.base.fetchAPI<ListResponse>(url, {
            method: "GET",
        });
    }

    async get(id: string): Promise<AudioDocument> {
        return await this.base.fetchAPI<AudioDocument>(`${this.route}/${id}`, {
            method: "GET",
        });
    }

    async getStream(
        audioDocumentId: string,
    ): Promise<{ url: string; durationSeconds: number }> {
        const response = await this.base.fetchAPI<{
            object: "audio_stream";
            url: string;
            durationSeconds: number;
        }>(`${this.route}/${audioDocumentId}/stream`, {
            method: "GET",
        });
        return {
            url: response.url,
            durationSeconds: response.durationSeconds,
        };
    }

    // Deprecated: Use getStream instead
    async getChunkStream(
        audioDocumentId: string,
        chunkIndex: number,
    ): Promise<ChunkStreamResponse> {
        return await this.base.fetchAPI<ChunkStreamResponse>(
            `${this.route}/${audioDocumentId}/chunks/${chunkIndex}/stream`,
            {
                method: "GET",
            },
        );
    }

    async updatePlayback(data: UpdatePlaybackParams): Promise<PlaybackState> {
        return await this.base.fetchAPI<PlaybackState>(
            `${this.route}/playback`,
            {
                method: "POST",
                body: {
                    audioDocumentId: data.audioDocumentId,
                    currentChunkIndex: data.currentChunkIndex,
                    positionSeconds: data.positionSeconds,
                },
            },
        );
    }
}

export const useService = () => {
    const base = useBaseService();

    return useMemo(() => new Service(base, "/audio-documents"), [base]);
};
